import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { api } from '../../../services/api';
import { session } from '../../../services/session';
import { dateService } from '../../../services/date';

import StrategyStatus from '../../ui/strategy-status';
import { TabbedCards, Tab } from '../../ui/tabs';
import DetailBanner from '../campaign-tracking/detail-banner';
import { determineGranularities, isEvergreen } from '../campaign-tracking/utils';
import BreadcrumbNav from '../../ui/breadcrumb/breadcrumb';
import StrategyPerformanceSummary from '../campaign-tracking/strategy-performance-summary';
import TopStrategyTab from './strategy-tab';

import './campaign-performance.scss';
import { Granularities } from '../../base/megatable/models';
import linkoutIcon from '../../../images/icons/linkout.svg';
import Tooltip from '../../misc/tooltip';

const conversionTypeLabels = {
  ImpactRadiusSubIds: 'Impact Radius',
  Linkshare: 'Linkshare',
  PepperJam: 'PepperJam',
  Default: 'JSTag',
};

const { defaultGranularities } = new Granularities();

const publishersColumns = [
  {
    label: 'Publisher',
    name: 'pub_name',
    flexWidth: true,
    cellRender: ({ cellData, rowData }) => (
      <div style={{ whiteSpace: 'normal' }}>
        <Link to={`stories?pub_ids=${rowData.pub_id}`}>{cellData}</Link>
      </div>
    ),
    tooltip: () => ({ text: 'View Top Stories for this Publisher', size: 120, fixedSize: true }),
  },
  {
    label: '# of Stories',
    name: 'number_of_stories',
    justifyContent: 'right',
  },
  {
    label: 'Spend',
    name: 'merchant_spend',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
  {
    label: 'Revenue',
    name: 'attributed_revenue',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
  {
    label: 'Units Sold',
    name: 'attributed_product_count',
    justifyContent: 'right',
    width: 100,
    //	Blank formats represent the need to only add in commas
    //	State explicitly? Almost all values need commas
    format: {},
  },
  {
    label: 'Impressions',
    name: 'impressions',
    justifyContent: 'right',
    width: 150,
    format: {},
  },
  {
    label: 'Clicks',
    name: 'clicks',
    justifyContent: 'right',
    width: 100,
    format: {},
  },
  {
    label: 'CTR',
    name: 'click_through_rate',
    justifyContent: 'right',
    width: 100,
    format: { type: 'percent', options: { precision: 2 } },
  },
  {
    label: 'ROI',
    name: 'roi',
    justifyContent: 'right',
    width: 60,
    format: { options: { precision: 1 } },
  },
  {
    label: 'CVR',
    name: 'order_conversion_rate',
    justifyContent: 'right',
    width: 80,
    format: { type: 'percent', options: { precision: 2 } },
  },
];

const storiesColumns = [
  {
    label: 'Publisher',
    name: 'pub_name',
  },
  {
    label: 'Story',
    name: 'story_name',
    width: 400,
    justifyContent: 'left',
    flexWidth: true,
    cellRender: ({ cellData, rowData }) => (
      //	TODO: Refactor out into own story_name_cell.js
      <div className="story-name-cell">
        <Link to={`recommendations?edit_ids=${rowData.edit_id}`}>{cellData}</Link>
        {rowData.story_url && (
          <a
            className="linkout"
            href={rowData.story_url}
            ref={node => new Tooltip(node, { text: 'Go to Article', size: 4 })}
          >
            <img src={linkoutIcon} className="link-out" alt="Link to story" />
          </a>
        )}
      </div>
    ),
    tooltip: () => ({
      text: 'View Top Recommendations for this Story',
      size: 120,
      fixedSize: true,
    }),
  },
  //	Check with backend
  // {label: 'Link Created', name: ''},
  {
    label: 'Spend',
    name: 'merchant_spend',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
  {
    label: 'Revenue',
    name: 'attributed_revenue',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
  {
    label: 'Units Sold',
    name: 'attributed_product_count',
    justifyContent: 'right',
    width: 120,
    format: {},
  },
  {
    label: 'Impressions',
    name: 'impressions',
    justifyContent: 'right',
    width: 120,
    format: {},
  },
  {
    label: 'Clicks',
    name: 'clicks',
    justifyContent: 'right',
    width: 120,
    format: {},
  },
  {
    label: 'CTR',
    name: 'click_through_rate',
    justifyContent: 'right',
    width: 120,
    format: { type: 'percent', options: { precision: 2 } },
  },
  {
    label: 'ROI',
    name: 'roi',
    justifyContent: 'right',
    width: 80,
    format: { options: { precision: 1 } },
  },
  {
    label: 'CVR',
    name: 'order_conversion_rate',
    justifyContent: 'right',
    width: 80,
    format: { type: 'percent', options: { precision: 2 } },
  },
];

const recommendationsColumns = [
  {
    label: 'Recommended Product',
    name: 'brand_product_name',
    width: 550,
    flexWidth: true,
    cellRender: ({ cellData, rowData }) => (
      //	TODO: See if white-space implementations can be standardized
      <div style={{ whiteSpace: 'normal' }}>
        <Link
          to={`products?auction_ids=${rowData.auction_id}&product_ids=${rowData.merchant_product_id}`}
        >
          {cellData}
        </Link>
      </div>
    ),
    tooltip: () => ({
      text: 'View Top Products for this Recommendation',
      size: 120,
      fixedSize: true,
    }),
  },
  {
    label: 'Publisher',
    name: 'pub_name',
  },
  {
    label: 'Story',
    name: 'story_name',
    width: 400,
    justifyContent: 'left',
    flexWidth: true,
    cellRender: ({ cellData, rowData }) => (
      <div className="story-name-cell">
        <span>{cellData}</span>
        {rowData.story_url && (
          <a
            className="linkout"
            href={rowData.story_url}
            ref={node => new Tooltip(node, { text: 'Go to Article', size: 4 })}
          >
            <img src={linkoutIcon} className="link-out" alt="Link to story" />
          </a>
        )}
      </div>
    ),
  },
  {
    label: 'Spend',
    name: 'merchant_spend',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
  {
    label: 'Revenue',
    name: 'attributed_revenue',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
  {
    label: 'Units Sold',
    name: 'attributed_product_count',
    justifyContent: 'right',
    width: 120,
    format: {},
  },
  {
    label: 'Impressions',
    name: 'impressions',
    justifyContent: 'right',
    width: 150,
    format: {},
  },
  {
    label: 'Clicks',
    name: 'clicks',
    justifyContent: 'right',
    width: 120,
    format: {},
  },
  {
    label: 'CTR',
    name: 'click_through_rate',
    justifyContent: 'right',
    width: 120,
    format: { type: 'percent', options: { precision: 2 } },
  },
  {
    label: 'CVR',
    name: 'order_conversion_rate',
    justifyContent: 'right',
    width: 80,
    format: { type: 'percent', options: { precision: 2 } },
  },
];

const productsColumns = [
  {
    label: 'Brand',
    name: 'brand',
  },
  {
    label: 'Product Purchased',
    name: 'brand_product_name',
    width: 550,
    flexWidth: true,
    cellRender: ({ cellData }) => <div style={{ whiteSpace: 'normal' }}>{cellData}</div>,
  },
  {
    label: 'Price',
    name: 'unit_price',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
  {
    label: 'Units Sold',
    name: 'attributed_product_count',
    justifyContent: 'right',
    width: 100,
  },
  {
    label: 'Revenue',
    name: 'attributed_revenue',
    justifyContent: 'right',
    format: { type: 'currency', options: { precision: 0 } },
  },
];

const CampaignPerformance = () => {
  const { merchantId, strategyId } = useParams();
  const flags = useFlags();

  const campaignPageUrl = `/v2/merchant/${session.orgId}/megatables/campaign-performance`;

  const [overviewData, setOverview] = useState({});
  const [strategyDateRange, setStrategyDateRange] = useState(null);
  const [dateFilterRange, setDateFilterRange] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([
    {
      label: 'Your Campaign Performance',
      href: campaignPageUrl,
    },
  ]);
  const [granularities, setGranularities] = useState([]);
  const rootURL = `/api/v0/merchants/${merchantId}/strategies/${strategyId}`;
  const strategyIsEvergreen = isEvergreen(strategyId);

  useEffect(() => {
    api.get(`${rootURL}/overview/`).then(resp => {
      const { stats } = resp.data.data[0];
      const config = stats.affiliate_config || {};

      stats.conversions_captured = conversionTypeLabels[config.default] || 'JSTag';

      setStrategyDateRange({
        date_from: moment.utc(stats.datetime_start),
        date_to: stats.datetime_end ? moment.utc(stats.datetime_end) : moment(),
      });

      const startingDateRange = getStrategyDateRange(stats);

      setDateFilterRange({
        date_from: startingDateRange.startDate.format('YYYY-MM-DD'),
        date_to: startingDateRange.endDate.format('YYYY-MM-DD'),
      });

      const granularity = determineGranularities(startingDateRange, defaultGranularities);
      setGranularities(granularity);
      setOverview(stats);
      addCrumb({ label: stats.name });
    });
  }, []);

  useEffect(() => {
    if (!dateFilterRange || granularities.length === 0) return;

    const granularity = determineGranularities(
      {
        startDate: dateService.dateFromStr(dateFilterRange.date_from),
        endDate: dateService.dateFromStr(dateFilterRange.date_to),
      },
      granularities,
    );

    setGranularities(granularity);
  }, [dateFilterRange]);

  //	Subject to change
  const addCrumb = (crumb, previousHref) => {
    const newTrail = breadcrumb.slice();

    if (previousHref) {
      const lastCrumb = newTrail.pop();
      lastCrumb.href = previousHref;
      newTrail.push(lastCrumb);
    }

    newTrail.push(crumb);
    setBreadcrumb(newTrail);
  };

  const setDates = (startDate, endDate) => {
    setDateFilterRange({
      date_from: startDate,
      date_to: endDate,
    });
  };
  const getStrategyDateRange = stats => {
    if (!stats.strategy_status) return null;

    const dateFrom = strategyIsEvergreen
      ? dateService.getFirstOfMonth(moment())
      : moment.utc(stats.datetime_start);

    const dateTo = stats.strategy_status === 'expired' ? moment.utc(stats.datetime_end) : moment();

    const dateRange = { startDate: dateFrom, endDate: dateTo };

    return dateRange;
  };

  const getDateRangeLabel = () => {
    if (!strategyDateRange) return null;

    const start = strategyIsEvergreen
      ? 'Ongoing'
      : strategyDateRange.date_from.format('MMM D, YYYY');

    const end =
      strategyIsEvergreen || !overviewData.datetime_end
        ? 'No end date'
        : strategyDateRange.date_to.format('MMM D, YYYY');

    return `${start} - ${end}`;
  };

  const getAdditionalStatus = status => {
    const today = moment();
    if (status === 'running_and_active' && overviewData.datetime_end) {
      const remainingTime =
        dateService.subtractDates(today, moment(overviewData.datetime_end), 'day') - 1;
      return ` (${Math.floor(remainingTime)} days left)`;
    }

    if (overviewData.strategy_status === 'running_and_paused' && overviewData.paused_on) {
      return ` as of ${moment(overviewData.paused_on).format('MMM D, YYYY')}`;
    }

    return null;
  };

  //	Exception format for Date Picker
  const defaultDates = dateFilterRange
    ? {
        startDate: dateService.dateFromStr(dateFilterRange.date_from),
        endDate: dateService.dateFromStr(dateFilterRange.date_to),
      }
    : null;

  return (
    <div className="campaign-performance-container">
      <div className="breadcrumb-container">
        <BreadcrumbNav crumbTrail={breadcrumb} />
      </div>
      <div className="header">
        <div className="name">{overviewData.name}</div>
        <div className="details">
          <StrategyStatus status={overviewData.strategy_status} />
          {getAdditionalStatus(overviewData.strategy_status)}
          <span className="divider">|</span>
          <span className="date-range">{getDateRangeLabel(strategyId)}</span>
        </div>
      </div>

      {session.isAdminView && <DetailBanner strategyId={strategyId} propData={overviewData} />}

      <div className="body">
        <TabbedCards styleType="block" fallbackPath="overview">
          <Tab
            path="overview"
            name="Performance Overview"
            isShowing={true}
            render={() => (
              <div className="strategy-performance">
                {defaultDates && (
                  <StrategyPerformanceSummary
                    strategyId={strategyId}
                    defaultDates={defaultDates}
                    pastDateThreshold={strategyIsEvergreen ? null : strategyDateRange.date_from}
                    futureLimit={strategyDateRange.date_to}
                  />
                )}
              </div>
            )}
          />
          <Tab
            path="publishers"
            name="Top Publishers"
            isShowing={flags.bestBuyAdminFlag}
            render={() => (
              <TopStrategyTab
                title="Publishers"
                name="publishers"
                dateRange={strategyDateRange}
                dateFilterRange={dateFilterRange}
                setDateFilterRange={setDates}
                granularities={granularities}
                setGranularities={setGranularities}
                rowKeyField="pub_id"
                graphParamKey="pub_ids"
                displayNameField="pub_name"
                columns={publishersColumns}
                endpoints={{
                  graph: `${rootURL}/interval_performance/`,
                  aggregate: `${rootURL}/aggregated_pubs_performance/`,
                  table: `${rootURL}/pubs_performance/`,
                }}
                filterByPublisher={true}
                reportType="top-pubs"
                stickyColumnsCount={1}
              />
            )}
          />
          <Tab
            path="stories"
            name="Top Stories"
            isShowing={flags.bestBuyAdminFlag}
            render={() => (
              <TopStrategyTab
                title="Stories"
                name="stories"
                dateRange={strategyDateRange}
                dateFilterRange={dateFilterRange}
                setDateFilterRange={setDates}
                granularities={granularities}
                setGranularities={setGranularities}
                rowKeyField="edit_id"
                graphParamKey="edit_ids"
                displayNameField="story_name"
                columns={storiesColumns}
                endpoints={{
                  graph: `${rootURL}/interval_performance/`,
                  aggregate: `${rootURL}/aggregated_stories_performance/`,
                  table: `${rootURL}/stories_performance/`,
                  breadcrumb: '/api/v0/publishers/publisher_info/',
                }}
                filterByPublisher={true}
                reportType="top-stories"
                linkedEntityInfo={{
                  name: 'publishers',
                  paramField: 'pub_ids',
                  displayField: 'pub_name',
                }}
                stickyColumnsCount={2}
              />
            )}
          />
          <Tab
            path="recommendations"
            name="Top Recommendations"
            isShowing={flags.bestBuyAdminFlag}
            render={() => (
              <TopStrategyTab
                title="Recommendations"
                name="recommendations"
                dateRange={strategyDateRange}
                dateFilterRange={dateFilterRange}
                setDateFilterRange={setDates}
                granularities={granularities}
                setGranularities={setGranularities}
                rowKeyField="auction_id"
                graphParamKey="auction_ids"
                displayNameField="brand_product_name"
                columns={recommendationsColumns}
                endpoints={{
                  graph: `${rootURL}/interval_performance/`,
                  aggregate: `${rootURL}/aggregated_recommendations_performance/`,
                  table: `${rootURL}/recommendations_performance/`,
                  breadcrumb: '/api/v0/publishers/edit_info/',
                }}
                filterByPublisher={true}
                reportType="strategy-tab-recommendations"
                linkedEntityInfo={{
                  name: 'edits',
                  paramField: 'edit_ids',
                  displayField: 'story_name',
                }}
                stickyColumnsCount={1}
              />
            )}
          />
          <Tab
            path="products"
            name="Top Products Purchased"
            isShowing={flags.bestBuyAdminFlag}
            render={() => (
              <TopStrategyTab
                title="Products Purchased"
                name="products"
                dateRange={strategyDateRange}
                dateFilterRange={dateFilterRange}
                setDateFilterRange={setDates}
                granularities={granularities}
                setGranularities={setGranularities}
                rowKeyField="merchant_product_id"
                graphParamKey="product_ids"
                displayNameField="brand_product_name"
                columns={productsColumns}
                endpoints={{
                  graph: `${rootURL}/products_interval_performance/`,
                  aggregate: `${rootURL}/aggregated_products_performance/`,
                  table: `${rootURL}/products_performance/`,
                  breadcrumb: '/api/v0/merchants/product_info/',
                }}
                filterByPublisher={false}
                reportType="strategy-tab-products"
                linkedEntityInfo={{
                  name: 'products',
                  paramField: 'auction_ids',
                  displayField: 'brand_product_name',
                }}
                stickyColumnsCount={2}
              />
            )}
          />
        </TabbedCards>
      </div>
    </div>
  );
};

export default CampaignPerformance;
